'use client';

import {
  Children,
  type ElementType,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
} from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { Slot } from '../slot';
import { iconRecipe } from './styles/icon.styles';
import type { IconPropsInterface } from './types/icon.types';

/**
 * Icon is a base wrapper used to provide functionality and styling to SVG icon components.
 *
 * @since 6.4.0
 */
export const Icon = forwardRef<SVGSVGElement, IconPropsInterface>(
  (props: IconPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      focusable = false,
      role = 'img',
      render,
      size,
      title,
      ...rest
    } = props;

    const variants = splitResponsiveVariant('size', size);
    const styles = css(iconRecipe.raw({ ...variants }), cssProp);

    const id = useId();
    const Component: ElementType = render || children ? Slot : 'svg';

    const iconChildren = useMemo(() => {
      if (!title) {
        return render ?? children;
      }

      const titleChildren = <title id={id}>{title}</title>;

      // Injects title into the render or children ReactNode
      const child = Children.toArray(render ?? children)[0];
      return isValidElement(child)
        ? // Cloning a RSC overrides internal children
          // so we spread its children (the svg paths) onto the clone
          cloneElement(child, undefined, titleChildren, ...(child.props?.children ?? []))
        : null;
    }, [title, id, render, children]);

    return (
      <Component
        aria-labelledby={title ? id : undefined}
        aria-hidden={title ? 'false' : 'true'}
        className={cx('c11n', styles, className)}
        data-c11n-component="Icon"
        data-c11n-version={__C11N_VERSION__}
        focusable={focusable}
        ref={forwardedRef}
        role={role}
        {...rest}
      >
        {iconChildren}
      </Component>
    );
  },
);

Icon.displayName = 'Icon';
